/**
 * Centralized HTTP client — use this instead of duplicating BACKEND_URL / axios instances.
 */
import axios from 'axios';
import {
  getApiBaseUrl,
  getBackendBaseUrl,
  isDesktopApp,
  isDesktopBackendReady,
} from '@/services/config/env';

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 750;
const RETRYABLE_STATUS = new Set([502, 503]);

const api = axios.create({
  timeout: 120_000,
});

// ── Demo-preview global interceptor ─────────────────────────────────────────
// Any request whose URL contains 'demo-preview' is silently swallowed so that
// the editor UI can be previewed without a real backend project.
api.interceptors.request.use((config) => {
  const url = String(config.url || '');
  if (url.includes('demo-preview')) {
    // Return a cancelled request that resolves to an empty success response.
    const controller = new AbortController();
    controller.abort();
    // Resolve via a synthetic axios-compatible response so callers don't throw.
    return Promise.reject({
      __demoPreview: true,
      config,
      response: { status: 200, data: {}, headers: {}, config },
      isAxiosError: false,
    });
  }
  return config;
});

// Swallow demo-preview "errors" so they never reach toast handlers.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.__demoPreview) return Promise.resolve(err.response);
    return Promise.reject(err);
  },
);
// ── End demo-preview interceptor ─────────────────────────────────────────────

api.interceptors.request.use((config) => {
  if (isDesktopApp() && !isDesktopBackendReady()) {
    return Promise.reject(new Error('Desktop backend is not ready'));
  }
  config.baseURL = getApiBaseUrl();

  if (isDesktopApp()) {
    config.headers['X-Twin-Tales-Desktop'] = '1';
  }

  const token = localStorage.getItem('storycraft_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const method = (config.method || 'get').toLowerCase();
  if (method === 'get') {
    config.headers['Cache-Control'] = 'no-cache';
    config.headers.Pragma = 'no-cache';
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const status = error.response?.status;

    if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.name === 'AbortError') {
      return Promise.reject(error);
    }

    // Only auto-retry safe/idempotent GETs. Retrying POST regenerate-image/video
    // after a gateway 502 or timeout starts a second generation while the first
    // may still succeed — false "failed" toasts + duplicate images.
    const method = String(config?.method || 'get').toLowerCase();
    const idempotent = method === 'get' || method === 'head' || method === 'options';
    if (
      config
      && idempotent
      && !config.__retryCount
      && (error.code === 'ECONNABORTED' || RETRYABLE_STATUS.has(status))
    ) {
      config.__retryCount = (config.__retryCount || 0) + 1;
      if (config.__retryCount <= MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * config.__retryCount));
        return api.request(config);
      }
    }

    if (status === 401) {
      localStorage.removeItem('storycraft_token');
      localStorage.removeItem('storycraft_user');
      const onAuthPage = isDesktopApp()
        ? /#\/(login|register|auth\/google\/callback)(\/|$|\?)/.test(window.location.hash || '')
        : ['/login', '/register', '/auth/google/callback', '/forgot-password', '/reset-password'].includes(
            window.location.pathname,
          );
      if (!onAuthPage) {
        window.location.href = isDesktopApp() ? '#/login' : '/login';
      }
    }
    return Promise.reject(error);
  }
);

export { getBackendBaseUrl, getApiBaseUrl };
export default api;
